import { Address } from 'viem';
import { mainnet, sepolia } from 'viem/chains';

import { VAULT_TRANCHE, VaultTrancheId } from '@/app/shared/constants/vaults';
import {
  ALLOWLIST_ADDRESS,
  HASTRA_JUNIOR_TRANCHE,
  HASTRA_MZUSD,
  HASTRA_SENIOR_TRANCHE,
  HASTRA_VAULT,
  JUNIOR_TRANCHE,
  MZUSD,
  SENIOR_TRANCHE,
  USDC,
  VAULT,
  WYLDS,
} from '@/app/shared/constants/web3/constants';
import { resolveTrancheId } from '@/app/shared/lib/tranche';

export type ChainContracts = {
  USDC: Address;
  ASSET: Address;
  MZUSD: Address;
  ALLOWLIST: Address;
  SENIOR_TRANCHE: Address;
  JUNIOR_TRANCHE: Address;
  VAULT?: Address;
};

type SharedContracts = {
  USDC: Address;
  ALLOWLIST: Address;
};

type VaultContracts = {
  VAULT: Address;
  MZUSD: Address;
  SENIOR_TRANCHE: Address;
  JUNIOR_TRANCHE: Address;
  ASSET: Address;
};

const SEPOLIA_SHARED: SharedContracts = {
  USDC: '0x88a7b291dC3F1dEd53c2067ccC5e628854EB7473',
  ALLOWLIST: '0x3123a85c87Ed92af29811bd3461c128407a0AdFa',
};

/** Sepolia vault deployments keyed by IssuanceVault address (lowercase). */
const SEPOLIA_VAULTS: Record<string, VaultContracts> = {
  // Vault 1 - Prime Loop / Hastra (testnet)
  '0xd95d5319e98e2583fb2c15a196cea58fabf8e07a': {
    VAULT: '0xd95d5319E98E2583fB2c15A196CEA58fAbf8e07a',
    MZUSD: '0x0e7DF600C70EcBC48965e838f26CbF75D6DB5556',
    SENIOR_TRANCHE: '0x82Babf0534812cb5a9665f2Ae1f57011367B3DF1',
    JUNIOR_TRANCHE: '0xE4F965045778Fb7EAA276947C3F69d85aFD961A0',
    ASSET: SEPOLIA_SHARED.USDC,
  },
  // Vault 2 - Mezzanine V2
  '0xff16b689c6e79a6dbae70ff4a67a0b06b6e040f3': {
    VAULT: '0xFF16B689C6E79a6dBAE70fF4a67a0B06B6E040f3',
    MZUSD: '0x175869F49abA18e7e53e0e49507B14aE808A576b',
    SENIOR_TRANCHE: '0x55c300548405422dc1d110af85645D30bbFa9261',
    JUNIOR_TRANCHE: '0x93712b96013bee786B9180A5DAd2d634bD3c4683',
    ASSET: SEPOLIA_SHARED.USDC,
  },
};

const MAINNET_SHARED: SharedContracts = {
  USDC,
  ALLOWLIST: ALLOWLIST_ADDRESS,
};

const MAINNET_VAULTS: Record<string, VaultContracts> = {
  [VAULT.toLowerCase()]: {
    VAULT,
    MZUSD,
    SENIOR_TRANCHE,
    JUNIOR_TRANCHE,
    ASSET: USDC,
  },
  [HASTRA_VAULT.toLowerCase()]: {
    VAULT: HASTRA_VAULT,
    MZUSD: HASTRA_MZUSD,
    SENIOR_TRANCHE: HASTRA_SENIOR_TRANCHE,
    JUNIOR_TRANCHE: HASTRA_JUNIOR_TRANCHE,
    ASSET: WYLDS,
  },
};

const normalizeAddress = (address?: string): string | undefined => address?.toLowerCase();

export const getSharedChainContracts = (chainId?: number): SharedContracts | undefined => {
  if (chainId === sepolia.id) return SEPOLIA_SHARED;
  if (chainId === mainnet.id) return MAINNET_SHARED;
  return undefined;
};

export const getVaultContracts = (
  chainId?: number,
  vaultAddress?: string,
): ChainContracts | undefined => {
  if (!chainId || !vaultAddress) return undefined;

  if (chainId === sepolia.id) {
    const vault = SEPOLIA_VAULTS[normalizeAddress(vaultAddress) ?? ''];
    if (!vault) return undefined;
    return {
      ...SEPOLIA_SHARED,
      ...vault,
    };
  }

  if (chainId === mainnet.id) {
    const vault = MAINNET_VAULTS[normalizeAddress(vaultAddress) ?? ''];
    if (!vault) return undefined;
    return {
      ...MAINNET_SHARED,
      ...vault,
    };
  }

  return undefined;
};

export type ChainMzusdToken = {
  vaultAddress: Address;
  mzusd: Address;
};

export const getChainMzusdTokens = (chainId?: number): ChainMzusdToken[] => {
  if (chainId === sepolia.id) {
    return Object.values(SEPOLIA_VAULTS).map((vault) => ({
      vaultAddress: vault.VAULT,
      mzusd: vault.MZUSD,
    }));
  }
  if (chainId === mainnet.id) {
    return Object.values(MAINNET_VAULTS).map((vault) => ({
      vaultAddress: vault.VAULT,
      mzusd: vault.MZUSD,
    }));
  }
  return [];
};

export const vaultUsesWyldsAsset = (contracts?: ChainContracts): boolean =>
  !!contracts && contracts.ASSET.toLowerCase() === WYLDS.toLowerCase();

export const getTrancheAddressById = (
  contracts: ChainContracts,
  trancheId: VaultTrancheId,
): Address | undefined => {
  if (trancheId === VAULT_TRANCHE.SENIOR) return contracts.SENIOR_TRANCHE;
  if (trancheId === VAULT_TRANCHE.JUNIOR) return contracts.JUNIOR_TRANCHE;
  return undefined;
};

export const getTrancheAddressByLabel = (
  contracts: ChainContracts,
  label?: string,
): Address | undefined => {
  const trancheId = resolveTrancheId(label);
  return trancheId ? getTrancheAddressById(contracts, trancheId) : undefined;
};
